/**
 * Passkey ceremonies in the browser (docs/members.md "People and passkeys"): the server's options,
 * the phone's prompt through @simplewebauthn/browser (loaded only when a ceremony starts, so the
 * gate's first paint does not carry it), and the server's answer. Words and rules are
 * src/passkeys-ui.ts.
 */
import { api } from "./api";
import type { AuthState } from "../shared/types";

type Browser = typeof import("@simplewebauthn/browser");
let browser: Promise<Browser> | null = null;
function load(): Promise<Browser> {
  browser ??= import("@simplewebauthn/browser");
  return browser;
}

/** The browser has WebAuthn at all. */
export function browserHasWebAuthn(): boolean {
  return typeof window !== "undefined" && typeof window.PublicKeyCredential === "function";
}

/** The browser can offer passkeys in the password field's suggestions (conditional mediation). */
export async function autofillAvailable(): Promise<boolean> {
  if (!browserHasWebAuthn()) return false;
  const pkc = window.PublicKeyCredential as unknown as { isConditionalMediationAvailable?: () => Promise<boolean> };
  try {
    return typeof pkc.isConditionalMediationAvailable === "function" && (await pkc.isConditionalMediationAvailable());
  } catch {
    return false;
  }
}

/**
 * Signs in with a passkey: `autofill` waits quietly for one picked from the password field's
 * suggestions, otherwise the phone asks at once. Starting a ceremony calls off any other still
 * waiting (the library keeps one AbortController for all of them).
 */
export async function signInWithPasskey(autofill = false): Promise<AuthState> {
  const { startAuthentication } = await load();
  const { challenge_id, options } = await api.passkeys.options("login");
  const credential = await startAuthentication({ optionsJSON: options as never, useBrowserAutofill: autofill });
  return api.passkeys.login(challenge_id, credential);
}

/** Adds a passkey for this device's peon (or the person it already is). Returns the AuthState after. */
export async function addPasskey(): Promise<AuthState> {
  const { startRegistration } = await load();
  const { challenge_id, options } = await api.passkeys.options("register");
  const credential = await startRegistration({ optionsJSON: options as never });
  return api.passkeys.register(challenge_id, credential);
}

/** Calls off a waiting ceremony (the gate's autofill request when the gate goes away). */
export function cancelPasskeyCeremony(): void {
  if (!browser) return;
  void browser.then((b) => b.WebAuthnAbortService.cancelCeremony());
}
